import { useState } from "react";
import { PageHero } from "@/components/public/Layout";
import { Reveal, SectionLabel, useSettings } from "@/components/public/shared";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { IMG } from "@/lib/images";
import { Phone, Mail } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const FIELD = "w-full rounded-none border-0 border-b border-subtle bg-transparent px-0 py-2 text-espresso focus-visible:ring-0 focus-visible:border-gold";
const TYPES = ["General Enquiry", "Event Catering", "Bulk Food Services", "Corporate Catering", "Hospitality Services", "Other"];

export default function Contact() {
  const s = useSettings();
  const [form, setForm] = useState({ requirement_type: "General Enquiry" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/enquiries/contact", form);
      toast.success(data.message || "Thank you. We'll be in touch shortly.");
      setForm({ requirement_type: "General Enquiry" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Submission failed");
    } finally { setLoading(false); }
  };

  return (
    <div>
      <PageHero label="Contact" title="Let's create something memorable." subtitle="Tell us about your occasion, organisation or requirement — our hospitality team will take it from there." image={IMG.fineTable} />
      <section className="bg-ivory py-20 sm:py-28">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-14 px-4 sm:px-6 lg:grid-cols-12 lg:px-8">
          <Reveal className="lg:col-span-5">
            <SectionLabel>Reach Us</SectionLabel>
            <h2 className="mt-5 font-display text-3xl font-light text-espresso sm:text-4xl">We'd love to hear from you</h2>
            <div className="mt-8 space-y-5">
              <a href={`tel:${(s.company_phone||"").replace(/\s/g,"")}`} className="flex items-center gap-4 text-espresso hover:text-gold" data-testid="contact-phone-link">
                <span className="grid h-11 w-11 place-items-center rounded-full border border-gold/40"><Phone className="h-5 w-5 text-gold" /></span>
                <span><span className="block text-[11px] uppercase tracking-wider text-inkmuted">Phone</span>{s.company_phone}</span>
              </a>
              <a href={`mailto:${s.company_email}`} className="flex items-center gap-4 text-espresso hover:text-gold">
                <span className="grid h-11 w-11 place-items-center rounded-full border border-gold/40"><Mail className="h-5 w-5 text-gold" /></span>
                <span><span className="block text-[11px] uppercase tracking-wider text-inkmuted">Email</span>{s.company_email}</span>
              </a>
            </div>
          </Reveal>

          <Reveal className="lg:col-span-7" delay={120}>
            <form onSubmit={submit} className="grid grid-cols-1 gap-x-8 gap-y-5 border border-subtle bg-cream p-8 sm:grid-cols-2" data-testid="contact-form">
              <div className="space-y-1.5"><Label className="text-[11px] uppercase tracking-wider text-inkmuted">Name *</Label>
                <Input required value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} className={FIELD} data-testid="contact-name" /></div>
              <div className="space-y-1.5"><Label className="text-[11px] uppercase tracking-wider text-inkmuted">Organisation</Label>
                <Input value={form.organisation || ""} onChange={(e) => setForm({ ...form, organisation: e.target.value })} className={FIELD} data-testid="contact-org" /></div>
              <div className="space-y-1.5"><Label className="text-[11px] uppercase tracking-wider text-inkmuted">Phone *</Label>
                <Input required value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={FIELD} data-testid="contact-phone" /></div>
              <div className="space-y-1.5"><Label className="text-[11px] uppercase tracking-wider text-inkmuted">Email *</Label>
                <Input required type="email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} className={FIELD} data-testid="contact-email" /></div>
              <div className="space-y-1.5 sm:col-span-2"><Label className="text-[11px] uppercase tracking-wider text-inkmuted">Requirement Type</Label>
                <Select value={form.requirement_type} onValueChange={(v) => setForm({ ...form, requirement_type: v })}>
                  <SelectTrigger className="rounded-none border-0 border-b border-subtle bg-transparent px-0" data-testid="contact-req-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="space-y-1.5 sm:col-span-2"><Label className="text-[11px] uppercase tracking-wider text-inkmuted">Message</Label>
                <Textarea rows={4} value={form.message || ""} onChange={(e) => setForm({ ...form, message: e.target.value })} className="rounded-md border border-subtle bg-transparent p-3 text-espresso focus-visible:ring-gold" data-testid="contact-message" /></div>
              <div className="sm:col-span-2">
                <button type="submit" disabled={loading} data-testid="contact-submit-button" className="w-full bg-espresso py-4 text-[12px] font-semibold uppercase tracking-editorial text-ivory transition-colors hover:bg-gold disabled:opacity-60">
                  {loading ? "Sending…" : "Send Enquiry"}
                </button>
              </div>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
